/**
 * Utility functions for product import
 */

/**
 * Estimates remaining time based on current progress
 * @param {number} current - Current processed rows
 * @param {number} total - Total rows to process
 * @param {number} startTime - Import start timestamp
 * @returns {Object} Time estimation in different formats
 */
export const estimateTimeRemaining = (current, total, startTime) => {
  if (current === 0) return { seconds: 0, formatted: 'Calculating...' };
  
  const elapsed = Date.now() - startTime;
  const rate = current / elapsed; // rows per millisecond
  const remainingMs = (total - current) / rate;
  const seconds = Math.round(remainingMs / 1000);
  
  // Format the time remaining
  if (seconds < 60) {
    return { seconds, formatted: `${seconds} seconds` };
  } else if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);
    return { seconds, formatted: `${minutes} minute${minutes > 1 ? 's' : ''}` };
  } else {
    const hours = Math.round(seconds / 3600);
    return { seconds, formatted: `${hours} hour${hours > 1 ? 's' : ''}` };
  }
};

/**
 * Validates a single field value
 * @param {string} fieldName - Name of the field to validate
 * @param {any} value - Value to validate
 * @returns {Promise<Object>} Validation result
 */
export const validateField = async (fieldName, value) => {
  try {
    const response = await fetch('/api/product_imports/validate_field', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ field: fieldName, value })
    });
    
    if (!response.ok) throw new Error('Validation request failed');
    return await response.json();
  } catch (error) {
    console.error('Field validation error:', error);
    return { valid: false, error: 'Validation failed' };
  }
};

/**
 * Debounces a function call
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
